import { type Campo, esValorDeLugar, rangoDeEscala, recorrer } from './campos'

/**
 * Las respuestas de un envío **como las lee una persona** (CLAUDE.md §9): con la etiqueta
 * de cada campo en vez de su clave, la opción elegida con su texto en vez de su valor
 * guardado, «Sí» y «No», y las fechas como se escriben aquí. Lo usan el correo del aviso,
 * la exportación a CSV y la ficha del envío del panel: tres sitios que tienen que decir
 * lo mismo, y por eso es una sola función (§24).
 *
 * Lo que se guarda no cambia: los valores siguen con sus claves, que es lo que sirve
 * para cruzar datos y lo que devuelve la exportación a JSON.
 */
export type FilaLegible = { clave: string; etiqueta: string; texto: string }

/**
 * En qué idioma se leen (CLAUDE.md §37): el aviso de una respuesta que llegó por la versión en inglés de una web
 * se lee en inglés. Solo cambia lo que escribe el sistema —sí y no, «3 de 5», números y fechas—; las etiquetas y
 * las opciones las escribió el cliente, y van tal cual. Por omisión, en español.
 */
export type IdiomaLegible = 'es' | 'en' | 'pt'
const PALABRAS: Record<IdiomaLegible, { si: string; no: string; de: string; locale: string }> = {
  es: { si: 'Sí', no: 'No', de: 'de', locale: 'es' },
  en: { si: 'Yes', no: 'No', de: 'of', locale: 'en-US' },
  pt: { si: 'Sim', no: 'Não', de: 'de', locale: 'pt-BR' },
}
const palabrasDe = (idioma: unknown) => PALABRAS[idioma === 'en' || idioma === 'pt' ? idioma : 'es']

const VACIO = '—'

/** «2026-03-15» → «15/03/2026» (en inglés, «03/15/2026»). Lo que no tenga esa forma, tal cual. */
export const fechaLegible = (valor: string, idioma?: IdiomaLegible): string => {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor)
  if (!partes) return valor
  return idioma === 'en' ? `${partes[2]}/${partes[3]}/${partes[1]}` : `${partes[3]}/${partes[2]}/${partes[1]}`
}

const etiquetaDeOpcion = (campo: Campo | undefined, valor: unknown): string => {
  const texto = String(valor ?? '')
  return campo?.opciones?.find((o) => o.valor === texto)?.etiqueta || texto
}

/** Una respuesta, en texto. `campo` puede faltar: un valor que ya no tiene campo (se borró del formulario) se enseña igual. */
export const textoDeRespuesta = (campo: Campo | undefined, valor: unknown, idioma?: IdiomaLegible): string => {
  const p = palabrasDe(idioma)
  if (valor === null || valor === undefined || valor === '') return VACIO
  if (Array.isArray(valor)) {
    if (valor.length === 0) return VACIO
    // Un repetidor: cada fila con las etiquetas de sus campos.
    if (valor.every((v) => v && typeof v === 'object' && !Array.isArray(v))) {
      const hijos = campo?.campos ?? []
      return valor
        .map((fila, i) => {
          const datos = fila as Record<string, unknown>
          const trozos = Object.entries(datos)
            .filter(([k]) => k !== 'id')
            .map(([k, v]) => {
              const hijo = hijos.find((h) => h.clave === k)
              return `${hijo?.etiqueta || k}: ${textoDeRespuesta(hijo, v, idioma)}`
            })
          return hijos.length > 0 || trozos.length > 0 ? `${i + 1}) ${trozos.join(', ')}` : `${i + 1})`
        })
        .join(' · ')
    }
    return valor.map((v) => etiquetaDeOpcion(campo, v)).join(', ')
  }
  if (typeof valor === 'boolean') return valor ? p.si : p.no
  // Un lugar se lee por su camino: «Perú › Lima › Lima › Miraflores». El que se escribió a mano, igual, con su país delante.
  if (esValorDeLugar(valor)) return String(valor.camino || [valor.pais, valor.texto ?? valor.codigo].filter(Boolean).join(' › ') || VACIO)
  if (typeof valor === 'number' && campo?.tipo === 'escala') return `${valor} ${p.de} ${rangoDeEscala(campo).maximo}`
  if (typeof valor === 'object') {
    return Object.entries(valor as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(', ')
  }
  if (typeof valor === 'number') return campo?.tipo === 'numero' || campo?.tipo === 'calculado' || campo?.tipo === 'valoracion' ? valor.toLocaleString(p.locale) : String(valor)
  const texto = String(valor)
  if (campo?.tipo === 'fecha') return fechaLegible(texto, idioma)
  if (campo?.tipo === 'desplegable' || campo?.tipo === 'opcion-multiple') return etiquetaDeOpcion(campo, texto)
  return texto
}

/**
 * Las filas de un envío, en el orden del formulario. Lo que el formulario ya no tiene
 * (un campo borrado después de que llegara el envío) va al final con su clave: es lo que
 * escribió una persona, y no se esconde.
 */
export const filasLegibles = (campos: Campo[], valores: Record<string, unknown>, idioma?: IdiomaLegible): FilaLegible[] => {
  const filas: FilaLegible[] = []
  const vistas = new Set<string>()
  for (const campo of campos) {
    if (!campo.clave || campo.tipo === 'paso' || campo.tipo === 'informativo' || vistas.has(campo.clave)) continue
    if (!(campo.clave in valores)) continue
    vistas.add(campo.clave)
    filas.push({ clave: campo.clave, etiqueta: campo.etiqueta || campo.clave, texto: textoDeRespuesta(campo, valores[campo.clave], idioma) })
  }
  for (const [clave, valor] of Object.entries(valores)) {
    if (vistas.has(clave)) continue
    filas.push({ clave, etiqueta: clave, texto: textoDeRespuesta(undefined, valor, idioma) })
  }
  return filas
}

/**
 * Los títulos de las columnas de una exportación: la etiqueta de cada clave, y si dos
 * campos se llaman igual, con su clave detrás para no confundirlos en la hoja.
 */
export const etiquetasDeColumnas = (campos: Campo[], claves: string[]): string[] => {
  const porClave = new Map<string, string>()
  for (const campo of recorrer(campos)) if (campo.clave && !porClave.has(campo.clave)) porClave.set(campo.clave, campo.etiqueta || campo.clave)
  const etiquetas = claves.map((c) => porClave.get(c) ?? c)
  const cuantas = new Map<string, number>()
  for (const e of etiquetas) cuantas.set(e, (cuantas.get(e) ?? 0) + 1)
  return etiquetas.map((e, i) => ((cuantas.get(e) ?? 0) > 1 ? `${e} (${claves[i]})` : e))
}

/** El campo de una clave, entrando en los repetidores. */
export const campoDeClave = (campos: Campo[], clave: string): Campo | undefined => {
  for (const campo of recorrer(campos)) if (campo.clave === clave) return campo
  return undefined
}

/**
 * La clave que sale de una etiqueta: «Teléfono de contacto» → «telefono_de_contacto». Sin
 * tildes ni guiones —en una fórmula serían una resta—, empezando por letra, y distinta de
 * las que ya hay (`_2`, `_3`…). Es lo que el panel propone y lo que el núcleo pone si
 * llega un campo sin clave.
 */
export const claveDesdeEtiqueta = (etiqueta: string, ocupadas: Iterable<string> = []): string => {
  const base =
    etiqueta
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 'campo_$1')
      .slice(0, 40)
      .replace(/_+$/, '') || 'campo'
  const usadas = new Set(ocupadas)
  if (!usadas.has(base)) return base
  let n = 2
  while (usadas.has(`${base}_${n}`)) n++
  return `${base}_${n}`
}
